import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { useLanguage } from '../../../../contexts/LanguageContext';
import GenericFileUpload from '../../../../components/common/GenericFileUpload';
import { useLazyFetchPartsQuery } from '../../../../api/endpoints/packageDefinitionsApi';
import { useLazyGetReturnReasonTypesQuery } from '../../../../api/endpoints/dealerPartsReturnProcess';
import useReturnRequestForm from '../hooks/useReturnRequestForm';
import PartSelectionSection from './PartSelectionSection';
import PartDisplaySection from './PartDisplaySection';
import ReturnRequestInfoSection from './ReturnRequestInfoSection';
import FaultyPartDetailsSection from './FaultyPartDetailsSection';
import { DEFAULT_LANGUAGE_ID, DEFAULT_PAGE_SIZE, FILE_UPLOAD_CONFIG, MIN_SEARCH_LENGTH, SEARCH_PAGE_SIZE } from '../constants/returnRequestConstants';

/**
 * İade talebi dialog bileşeni
 */
const ReturnRequestDialog = ({ visible, onHide, selectedPart, onSuccess, handleReturnRequestSubmit, isSubmitting, isDomestic }) => {
    const { th, getCurrentLanguageId } = useLanguage();
    const currentLanguageId = getCurrentLanguageId();
    const isForeignDealer = isDomestic === false;

    // Parça listesi fetch
    const [triggerFetchParts, { data: partsItems = [], isFetching: loadingParts }] = useLazyFetchPartsQuery();

    // İade nedeni tipleri fetch
    const [triggerFetchReasonTypes, { data: returnReasonTypes = [], isFetching: loadingReasonTypes }] = useLazyGetReturnReasonTypesQuery();

    // Debounce timer ref
    const filterTimeoutRef = useRef(null);

    // Filter value state (kontrollü filter için)
    const [filterValue, setFilterValue] = useState('');

    // Form hook'u
    const { formData, files, errors, submitted, setSubmitted, setFiles, handleInputChange, handlePartSelection, validateForm } = useReturnRequestForm(selectedPart, visible, th, isForeignDealer);

    // Parça seçenekleri
    const partOptions = useMemo(
        () =>
            (partsItems || []).map((i) => ({
                code: i.partCode,
                name: i.partName,
                uom: i.uom,
                label: `${i.partCode} – ${i.partName}`
            })),
        [partsItems]
    );

    // Dialog açıldığında verileri yükle
    useEffect(() => {
        if (visible) {
            // İade nedeni tiplerini yükle (dil değiştiğinde cache bypass)
            triggerFetchReasonTypes({ languageId: currentLanguageId }, true);

            // Yeni iade talebi için parça listesini yükle
            if (!selectedPart?.id) {
                triggerFetchParts({
                    q: '',
                    pageNumber: 1,
                    pageSize: DEFAULT_PAGE_SIZE,
                    languageId: currentLanguageId
                });
            }
        }

        // Dialog kapandığında filter'ı sıfırla
        if (!visible) {
            setFilterValue('');
        }
    }, [visible, selectedPart?.id, triggerFetchParts, triggerFetchReasonTypes, currentLanguageId]);

    /**
     * Form submit işlemi
     */
    const handleSubmit = async () => {
        setSubmitted(true);

        if (!validateForm()) {
            return;
        }

        await handleReturnRequestSubmit(formData, files, () => {
            onHide();
            onSuccess?.();
        });
    };

    /**
     * Parça seçimi değişikliği
     */
    const handlePartChange = (partCode) => {
        handlePartSelection(partCode, partOptions);
    };

    /**
     * Parça arama filter değişikliği
     * En az 3 karakter girildiğinde arama yapar, aksi halde default listeyi getirir
     */
    const handleFilterChange = useCallback(
        (value) => {
            // Filter değerini hemen güncelle
            setFilterValue(value);

            const trimmedValue = value.trim();

            // Önceki timer'ı temizle
            if (filterTimeoutRef.current) {
                clearTimeout(filterTimeoutRef.current);
            }

            filterTimeoutRef.current = setTimeout(() => {
                if (trimmedValue.length >= MIN_SEARCH_LENGTH) {
                    // 3+ karakter: arama yap
                    triggerFetchParts({
                        q: trimmedValue,
                        pageNumber: 1,
                        pageSize: SEARCH_PAGE_SIZE,
                        languageId: currentLanguageId
                    });
                } else {
                    // 3'ten az karakter: default listeyi getir
                    triggerFetchParts({
                        q: '',
                        pageNumber: 1,
                        pageSize: DEFAULT_PAGE_SIZE,
                        languageId: currentLanguageId
                    });
                }
            }, 300); // 300ms debounce
        },
        [triggerFetchParts, currentLanguageId]
    );

    /**
     * Dialog footer
     */
    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button label={th('common.cancel')} icon="pi pi-times" outlined onClick={onHide} disabled={isSubmitting} />
            <Button label={th('common.save')} icon="pi pi-check" onClick={handleSubmit} loading={isSubmitting} />
        </div>
    );

    const isNewRequest = !selectedPart?.id;

    return (
        <Dialog header={th('dealerPartsReturnProcess.returnRequest.title')} visible={visible} style={{ width: '70vw', maxWidth: '800px' }} footer={footer} onHide={onHide} maximizable>
            <div className="grid">
                {/* Parça Bilgileri */}
                <div className="col-12">
                    <h5 className="mt-2">{th('dealerPartsReturnProcess.returnRequest.partInformation')}</h5>
                </div>

                {isNewRequest ? (
                    <PartSelectionSection
                        formData={formData}
                        partOptions={partOptions}
                        errors={errors}
                        submitted={submitted}
                        loadingParts={loadingParts}
                        onPartChange={handlePartChange}
                        onFilterChange={handleFilterChange}
                        filterValue={filterValue}
                        th={th}
                    />
                ) : (
                    <PartDisplaySection selectedPart={selectedPart} th={th} />
                )}

                {/* İade Talebi Bilgileri */}
                <div className="col-12">
                    <h5 className="mt-2 mb-2">{th('dealerPartsReturnProcess.returnRequest.returnRequestInformation')}</h5>
                </div>

                <ReturnRequestInfoSection
                    formData={formData}
                    errors={errors}
                    submitted={submitted}
                    onInputChange={handleInputChange}
                    th={th}
                    returnReasonTypes={returnReasonTypes}
                    loadingReasonTypes={loadingReasonTypes}
                    showInvoiceNo={isForeignDealer}
                />

                {/* Arızalı Parça Detayları */}
                <FaultyPartDetailsSection formData={formData} errors={errors} submitted={submitted} onInputChange={handleInputChange} th={th} />

                {/* Dosya Yükleme */}
                <div className="col-12">
                    <div className="field">
                        <GenericFileUpload
                            newFiles={files}
                            onFilesChange={setFiles}
                            existingDocuments={[]}
                            multiple={true}
                            maxFileSize={FILE_UPLOAD_CONFIG.maxFileSize}
                            acceptedFileTypes={FILE_UPLOAD_CONFIG.acceptedFileTypes}
                            showDownloadButton={false}
                            showUpdateButton={false}
                            showDeleteButton={false}
                        />
                    </div>
                </div>
            </div>
        </Dialog>
    );
};

export default ReturnRequestDialog;
